import { useCallback, useEffect, useMemo, useState } from 'react'
import { initialHospitalData } from '../../HealthSystemDetails/constants'
import {
  ability,
  ACTIONS,
  ROLES,
  SUBJECTS,
  SubjectWithAttributes
} from '../../Permission'
import { TAddEditHospitalResponse } from 'components/Forms/forms.addEditHospital.d'
import { EditHospitalDetails } from './EditHospitalDetails/EditHospitalDetails'
import { updateLoadingStack } from '../../../redux/store/common/slice'
import { getHospitalData } from '../../HealthSystemDetails/HospitalsTable/api'
import { getHospitalDataOutsideCommunity } from '../MyOrganizationCommunity/api'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import { useParams } from 'react-router-dom'
import { Container } from 'components/Container/Container'
import {
  CREATE_EDIT_HOSPITAL_TABS,
  CREATE_HOSPITAL_BREADCRUMBS,
  OUTSIDE_COMMUNITY_HOSPITAL_BREADCRUMBS,
  VIEW_HOSPITAL_TABS
} from './constants'
import { UsersTable } from './UsersTable/UsersTable'
import { Menu } from '../../../components/Menu/Menu'
import {
  HOSPITAL_STATUS_TO_CLASSNAME,
  Status
} from '../../../components/Status'
import { HospitalActions } from './EditHospitalDetails/Actions'
import { LocationState } from '../HealthSystems/FullView/HospitalsTable/types'
import useRouter from '../../../hooks/useRouter'
import { GeneralHospitalDetails } from './GeneralHospitalDetails/GeneralHospitalDetails'
import { subject } from '@casl/ability'
import { defineAbility } from 'features/MyCommunity/MyOrganizationCommunity/Abilities'
import {
  ACTIONS as MY_COMMUNITY_ACTIONS,
  SUBJECTS as MY_COMMUNITY_SUBJECTS
} from 'features/MyCommunity/MyOrganizationCommunity/Abilities/constants'
import { myOrganizationCommunitySubject } from 'features/MyCommunity/MyOrganizationCommunity/Abilities/types'
import { BankingSetupContainer } from 'features/MyCommunity/BankingSetupCommunity/BankingSetupContainer'
import { HospitalDetailsBankAccountsContainer } from './HospitalDetailsBankAccounts/HospitalDetailsBankAccountsContainer'
import { TBusinessAccountDetails } from 'features/MyCommunity/BankingSetupCommunity/types'
import { getBusinessAccountDetailsRequest } from 'features/MyCommunity/BankingSetupCommunity/api'
import BankingSetupPopupProvider from 'features/MyCommunity/BankingSetupCommunity/Providers/BankingSetupPopupProvider'
import { FETCH_STATUS } from 'redux/store/common/types'

export const HospitalDetailsContainer = ({
  outsideCommunityMode = false
}: {
  outsideCommunityMode?: boolean
}) => {
  const { hospitalId, id, tab } = useParams()
  // Community hospital URLs include the health system as :id.
  // The outside-community URL is /hospitals/:hospitalId and has no :id,
  // so a missing route id is expected there and the check still passes.
  const hasHealthSystemId = useCallback(
    () => outsideCommunityMode || !!id,
    [outsideCommunityMode, id]
  )
  const { location } = useRouter()
  const locationState = location.state as LocationState
  const router = useRouter()
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState(
    tab || CREATE_EDIT_HOSPITAL_TABS[0].key
  )
  const [details, setDetails] =
    useState<TAddEditHospitalResponse>(initialHospitalData)
  const [businessAccountDetails, setBusinessAccountDetails] = useState<
    TBusinessAccountDetails | undefined
  >(undefined)

  const myCommunityAbility = useMemo(() => defineAbility(user), [user])

  const canEditHospitals =
    ability.can(ACTIONS.CRUD, SUBJECTS.HOSPITALS_IN_HS_DETAILS) ||
    ability.can(
      ACTIONS.EDIT,
      subject(SUBJECTS.HOSPITALS_IN_HS_DETAILS, {
        uuid:
          user.role === ROLES.HOSPITAL_ADMIN ? hospitalId : user.health_system
      } as unknown as SubjectWithAttributes)
    )

  //TODO: add permissions for edit user list in future
  const canSeeUsersList = ability.can(
    ACTIONS.VIEW,
    subject(SUBJECTS.USERS_IN_HOSPITAL_DETAILS, {
      uuid: user.role === ROLES.HOSPITAL_ADMIN ? hospitalId : user.health_system
    } as unknown as SubjectWithAttributes)
  )

  const canArchiveHospitals = ability.can(
    ACTIONS.ARCHIVE,
    SUBJECTS.HOSPITALS_IN_HS_DETAILS
  )

  const canSeeBankingSetup = myCommunityAbility.can(
    MY_COMMUNITY_ACTIONS.VIEW,
    myOrganizationCommunitySubject(
      MY_COMMUNITY_SUBJECTS.BANKING_SETUP_HOSPITAL_DETAILS,
      {
        uuid: hospitalId,
        status: details.status
      }
    )
  )

  const canSeeBankAccounts = myCommunityAbility.can(
    MY_COMMUNITY_ACTIONS.VIEW,
    myOrganizationCommunitySubject(
      MY_COMMUNITY_SUBJECTS.BANK_ACCOUNTS_HOSPITAL_DETAILS,
      {
        uuid: hospitalId,
        status: details.status,
        businessAccountStatus: businessAccountDetails?.verification_status
      }
    )
  )

  const handleHospitalForEdit = useCallback(() => {
    if (!hasHealthSystemId() || !hospitalId) return
    const healthSystemId = user.health_system
    const communityId = user.community
    if (outsideCommunityMode && !healthSystemId) return
    if (!outsideCommunityMode && (!communityId || !id)) return
    dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
    const request =
      outsideCommunityMode && healthSystemId
        ? getHospitalDataOutsideCommunity(healthSystemId, hospitalId)
        : getHospitalData(communityId as string, id as string, hospitalId)
    request
      .then((resp) => {
        setDetails(resp.data)
      })
      .catch(console.error)
      .finally(() =>
        dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
      )
  }, [
    dispatch,
    hospitalId,
    hasHealthSystemId,
    id,
    outsideCommunityMode,
    user.community,
    user.health_system
  ])

  const getBusinessAccountDetails = useCallback(async () => {
    if (!details.moov_business_account) return
    try {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
      const res = await getBusinessAccountDetailsRequest(
        details.moov_business_account
      )
      setBusinessAccountDetails(res.data)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
    }
  }, [details.moov_business_account, dispatch])

  const refreshHospitalAndBADetails = useCallback(() => {
    handleHospitalForEdit()
    getBusinessAccountDetails()
  }, [handleHospitalForEdit, getBusinessAccountDetails])

  useEffect(() => {
    if (hospitalId !== 'new') {
      handleHospitalForEdit()
    }
  }, [hospitalId, handleHospitalForEdit])

  useEffect(() => {
    if (details.moov_business_account) {
      getBusinessAccountDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.moov_business_account])

  const tabs = useMemo(() => {
    if (canSeeUsersList)
      return CREATE_EDIT_HOSPITAL_TABS.filter(
        (tab) =>
          (canSeeBankingSetup || tab.key !== 'banking_setup') &&
          (canSeeBankAccounts || tab.key !== 'bank_accounts')
      )
    else return VIEW_HOSPITAL_TABS
  }, [canSeeUsersList, canSeeBankingSetup, canSeeBankAccounts])

  const RenderedMenu = useMemo(() => {
    const handleSelectTab = (e) => {
      setActiveTab(e.key)
      router.push(e.key)
    }
    if (hospitalId === 'new') return null
    return (
      <Menu
        mode="horizontal"
        onSelect={handleSelectTab}
        selectedKeys={[activeTab]}
        defaultSelectedKeys={[activeTab]}
        items={tabs}
      />
    )
  }, [hospitalId, activeTab, tabs, router])
  const RenderedStatus = useMemo(
    () => (
      <Status.Default className={HOSPITAL_STATUS_TO_CLASSNAME[details.status]}>
        {details.status}
      </Status.Default>
    ),
    [details.status]
  )

  const RenderedActions = useMemo(
    () =>
      canArchiveHospitals && (
        <HospitalActions
          details={details}
          handleHospitalForEdit={handleHospitalForEdit}
          outsideCommunityMode={outsideCommunityMode}
        />
      ),
    [canArchiveHospitals, details, handleHospitalForEdit, outsideCommunityMode]
  )

  const breadcrumbs = useMemo(() => {
    const hsName = locationState?.hsName || details?.health_system_name
    if (outsideCommunityMode) {
      return OUTSIDE_COMMUNITY_HOSPITAL_BREADCRUMBS(
        hospitalId,
        hsName,
        details?.name
      )
    }
    return CREATE_HOSPITAL_BREADCRUMBS(hospitalId, hsName, id, details?.name)
  }, [hospitalId, details, id, locationState, outsideCommunityMode])

  const title = useMemo(() => {
    return hospitalId === 'new' ? 'Hospital creation' : details?.name
  }, [hospitalId, details?.name])

  return (
    <BankingSetupPopupProvider>
      <Container
        title={title}
        breadcrumbs={breadcrumbs}
        menu={RenderedMenu}
        status={RenderedStatus}
        actions={RenderedActions}
        className="view-edit-hs-container"
      >
        {activeTab === CREATE_EDIT_HOSPITAL_TABS[0].key && canEditHospitals && (
          <EditHospitalDetails
            handleHospitalForEdit={handleHospitalForEdit}
            details={details}
            outsideCommunityMode={outsideCommunityMode}
          />
        )}
        {activeTab === CREATE_EDIT_HOSPITAL_TABS[0].key &&
          !canEditHospitals && (
            <GeneralHospitalDetails
              details={details}
              hsId={outsideCommunityMode ? user.health_system : id}
            />
          )}
        {activeTab === CREATE_EDIT_HOSPITAL_TABS[1].key && canSeeUsersList && (
          <UsersTable />
        )}
        {activeTab === CREATE_EDIT_HOSPITAL_TABS[2].key && canSeeBankingSetup && (
          <BankingSetupContainer
            communityEntity={{
              entity_id: hospitalId || '',
              entity_type: 'hospital'
            }}
            businessAccountDetails={businessAccountDetails}
            moovBusinessAccountId={details.moov_business_account}
            refreshDetails={refreshHospitalAndBADetails}
          />
        )}
        {activeTab === CREATE_EDIT_HOSPITAL_TABS[3].key &&
          canSeeBankAccounts &&
          businessAccountDetails && (
            <HospitalDetailsBankAccountsContainer
              businessAccountDetails={businessAccountDetails}
              getBusinessAccountDetails={getBusinessAccountDetails}
            />
          )}
      </Container>
    </BankingSetupPopupProvider>
  )
}
